/**
 * useClientBacktest – client-side backtesting hook.
 *
 * Fetches candles (and optionally funding rates) from the REST API,
 * then delegates the full backtest computation to a Web Worker.
 * Results are written into the shared Zustand backtest store so all
 * existing dashboard panels render them automatically.
 */

import { useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useBacktestStore } from '../stores/backtestStore.js';
import { useAgentStatusStore } from '../stores/agentStatusStore.js';
import { getCandles, getFundingRates, getOpenInterest as fetchOpenInterest, getLongShortRatio as fetchLongShortRatio, submitClientBacktest } from '../api/client.js';
import type { WorkerInput, WorkerMessage } from '../workers/backtest-worker-types.js';
import type { ApiBacktestResult, Candle, Timeframe } from '../types.js';
import { queryKeys } from './useBacktest.js';
import { WARMUP_BARS } from '../../core/constants.js';
import { timeframeToMs } from '../../core/types.js';

// ---------------------------------------------------------------------------
// Pure helpers (exported for testing)
// ---------------------------------------------------------------------------

/**
 * Computes the fetch date/time ranges for a single-asset backtest.
 *
 * All four data series (candles, funding rates, open interest, long/short ratio)
 * must start from `warmupFetchStartMs` — WARMUP_BARS candles before `startDate`
 * — so that strategies which use funding/OI/LSR data on the first tradeable bar
 * see populated values instead of `null` from `findLatestBefore`.
 *
 * This matches the behaviour of the aggregation path and ensures single-asset
 * and aggregation results are comparable at the start of the backtest period.
 *
 * @param startDate ISO date string for the first tradeable bar (e.g. "2024-01-01")
 * @param endDate   ISO date string for the last tradeable bar (e.g. "2024-06-01")
 * @param timeframe Candlestick timeframe (e.g. "1h", "4h", "1d")
 * @returns         Millisecond timestamps and ISO string used for API calls
 */
export function computeFetchRanges(
  startDate: string,
  endDate: string,
  timeframe: string,
): {
  /** Start of the warmup window — all 4 data series are fetched from here. */
  warmupFetchStartMs: number;
  /** ISO string form of warmupFetchStartMs, used for the candles API call. */
  warmupFetchStartIso: string;
  /** Millisecond timestamp of the first tradeable bar (= new Date(startDate).getTime()). */
  startMs: number;
  /** Millisecond timestamp of the last tradeable bar (= new Date(endDate).getTime()). */
  endMs: number;
} {
  const startMs = new Date(startDate).getTime();
  const endMs = new Date(endDate).getTime();
  const warmupFetchStartMs = startMs - WARMUP_BARS * timeframeToMs(timeframe as Timeframe);
  return {
    warmupFetchStartMs,
    warmupFetchStartIso: new Date(warmupFetchStartMs).toISOString(),
    startMs,
    endMs,
  };
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface ClientBacktestProgress {
  current: number;
  total: number;
  percent: number;
}

export interface ClientBacktestInput {
  strategyCode: string;
  strategyName: string;
  symbol: string;
  timeframe: string;
  params: Record<string, unknown>;
  startDate: string; // ISO date string, e.g. "2024-01-01"
  endDate: string;   // ISO date string, e.g. "2024-06-01"
  initialCapital: number;
  exchange?: string;
  leverage?: number;
  takerFeeRate?: number;
  slippagePercent?: number;
  /** Trading mode. Defaults to 'futures'. */
  mode?: 'spot' | 'futures';
  /**
   * When true, the worker collects diagnostics for 0-trade analysis.
   * Only set to true for chat-triggered runs. Defaults to false.
   */
  collectDiagnostics?: boolean;
  /**
   * When false, skip fetching AND passing open-interest data to the worker.
   * Pass empty arrays instead. Defaults to true (fetch and pass OI).
   * Set to false for grid-search combo reruns to reproduce the same auxiliary-data
   * profile as the grid-search worker (which does not use open interest).
   */
  includeOpenInterest?: boolean;
  /**
   * When false, skip fetching AND passing long/short-ratio data to the worker.
   * Pass empty arrays instead. Defaults to true (fetch and pass LSR).
   * Set to false for grid-search combo reruns to reproduce the same auxiliary-data
   * profile as the grid-search worker (which does not use long/short ratio).
   */
  includeLongShortRatio?: boolean;
}

/** Options for the useClientBacktest hook. */
export interface ClientBacktestOptions {
  /** Worker execution timeout in milliseconds. Defaults to 60 000 ms (60 s). */
  timeoutMs?: number;
}

interface ClientBacktestState {
  running: boolean;
  progress: ClientBacktestProgress | null;
  error: string | null;
}

const DEFAULT_TIMEOUT_MS = 60_000;

// ---------------------------------------------------------------------------
// Helper: build a BacktestResult-compatible object from WorkerResult + input
// ---------------------------------------------------------------------------

function buildBacktestResult(
  workerResult: import('../workers/backtest-worker-types.js').WorkerResult,
  input: ClientBacktestInput,
  candles: Candle[],
  startedAt: number,
): ApiBacktestResult {
  return {
    id: `browser-${startedAt}`,
    config: {
      id: `browser-config-${startedAt}`,
      strategyName: input.strategyName,
      params: input.params,
      symbol: input.symbol,
      timeframe: input.timeframe as Timeframe,
      startDate: new Date(input.startDate).getTime(),
      endDate: new Date(input.endDate).getTime(),
      initialCapital: input.initialCapital,
      exchange: input.exchange ?? 'bybit',
    },
    trades: workerResult.trades,
    metrics: workerResult.metrics,
    equity: workerResult.equity,
    // Forward diagnostics from worker result (present when collectDiagnostics=true)
    ...(workerResult.diagnostics !== undefined
      ? { diagnostics: workerResult.diagnostics }
      : {}),
    // Forward strategy logs from worker result (single backtests only)
    ...(workerResult.strategyLogs !== undefined
      ? { strategyLogs: workerResult.strategyLogs }
      : {}),
    candles,
    createdAt: startedAt,
    duration: Date.now() - startedAt,
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useClientBacktest(options: ClientBacktestOptions = {}) {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  const [state, setState] = useState<ClientBacktestState>({
    running: false,
    progress: null,
    error: null,
  });

  const workerRef = useRef<Worker | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { setResult, setRunning, setError: setStoreError } = useBacktestStore();
  const queryClient = useQueryClient();

  /** Clear the running timeout if one is active. */
  const clearWorkerTimeout = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const run = useCallback(
    async (input: ClientBacktestInput): Promise<ApiBacktestResult> => {
      // Terminate any existing worker before starting a new one
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
      clearWorkerTimeout();

      setState({ running: true, progress: null, error: null });
      setRunning(true);

      // Generate an idempotency key for this logical run.
      // The key is generated here (before any async work) so the same key is
      // reused on any retry of the persistence POST — preventing duplicate rows.
      const idempotencyKey = crypto.randomUUID();

      const startedAt = Date.now();
      const exchange = input.exchange ?? 'bybit';

      // candles needs to be accessible in the worker's onmessage closure
      let fetchedCandles: Candle[] = [];
      let fundingRates: Array<{ timestamp: number; fundingRate: number; markPrice?: number }> | undefined;
      let openInterestRecords: Array<{ timestamp: number; openInterestAmount: number }> = [];
      let longShortRatioRecords: Array<{ timestamp: number; longRatio: number; shortRatio: number; longShortRatio: number }> = [];

      try {
        // ── 1. Compute fetch ranges ─────────────────────────────────────────
        // ALL four data series (candles, funding rates, OI, LSR) start from
        // warmupFetchStartMs so that strategies using auxiliary data on the first
        // tradeable bar see populated values instead of null from findLatestBefore.
        // This matches the aggregation path and ensures parity (NF-5).
        const { warmupFetchStartMs, warmupFetchStartIso, endMs } = computeFetchRanges(
          input.startDate,
          input.endDate,
          input.timeframe,
        );

        // ── 2. Fetch candles ────────────────────────────────────────────────
        // Extend the fetch start by WARMUP_BARS candles so indicators are primed
        // before trading begins at config.startDate. The worker only trades bars
        // >= config.startDate, so the warmup candles prime indicators only.
        fetchedCandles = await getCandles({
          exchange,
          symbol: input.symbol,
          timeframe: input.timeframe as Timeframe,
          startDate: warmupFetchStartIso,
          endDate: input.endDate,
        });

        if (!fetchedCandles.length) {
          throw new Error('No candles found for the selected date range');
        }

        // ── 3. Fetch funding rates ──────────────────────────────────────────
        // NF-5: start from warmupFetchStartMs (same window as candles) so
        // findLatestBefore can resolve data on the first tradeable bars.
        const frResponse = await getFundingRates({
          exchange,
          symbol: input.symbol,
          start: warmupFetchStartMs,
          end: endMs,
        });
        fundingRates = frResponse.rates;

        // ── 4. Fetch open interest & long/short ratio in parallel ──────────
        // Both default to true so existing callers are unchanged.
        // Set either to false to skip fetching and pass empty arrays to the worker
        // (used by grid-search combo reruns to reproduce the same auxiliary-data
        // profile as the grid-search worker, which does not use OI / LSR).
        // NF-5: start from warmupFetchStartMs for the same reason as funding rates.
        const includeOI = input.includeOpenInterest !== false;
        const includeLSR = input.includeLongShortRatio !== false;

        if (includeOI || includeLSR) {
          const [oiResponse, lsrResponse] = await Promise.all([
            includeOI
              ? fetchOpenInterest({ exchange, symbol: input.symbol, start: warmupFetchStartMs, end: endMs })
                .catch(() => ({ records: [] as Array<{ timestamp: number; openInterestAmount: number }> }))
              : Promise.resolve({ records: [] as Array<{ timestamp: number; openInterestAmount: number }> }),
            includeLSR
              ? fetchLongShortRatio({ exchange, symbol: input.symbol, start: warmupFetchStartMs, end: endMs })
                .catch(() => ({ records: [] as Array<{ timestamp: number; longRatio: number; shortRatio: number; longShortRatio: number }> }))
              : Promise.resolve({ records: [] as Array<{ timestamp: number; longRatio: number; shortRatio: number; longShortRatio: number }> }),
          ]);

          openInterestRecords = oiResponse.records;
          longShortRatioRecords = lsrResponse.records;
        }
        // If both are false, openInterestRecords and longShortRatioRecords stay as empty arrays
      } catch (err) {
        clearWorkerTimeout();
        const errorMsg = err instanceof Error ? err.message : String(err);
        setStoreError(errorMsg);
        setState({ running: false, progress: null, error: errorMsg });
        throw err;
      }

      // Capture candles in closure for result building
      const candlesForResult = fetchedCandles;

      // ── 3. Wrap worker lifecycle in a Promise ─────────────────────────────
      return new Promise<ApiBacktestResult>((resolve, reject) => {
        // Guard against race conditions: only the first terminal event (success,
        // error, or timeout) processes. Subsequent events are ignored.
        let settled = false;

        const worker = new Worker(
          new URL('../workers/backtest.worker.ts', import.meta.url),
          { type: 'module' },
        );
        workerRef.current = worker;

        // ── 4. Start timeout watchdog ─────────────────────────────────────
        timeoutRef.current = setTimeout(() => {
          if (settled) return;
          settled = true;
          const errorMsg = `Backtest timed out after ${Math.round(timeoutMs / 1000)}s`;
          worker.terminate();
          workerRef.current = null;
          timeoutRef.current = null;
          setStoreError(errorMsg);
          setState({ running: false, progress: null, error: errorMsg });
          reject(new Error(errorMsg));
        }, timeoutMs);

        worker.onmessage = (e: MessageEvent<WorkerMessage>) => {
          const msg = e.data;

          if (msg.type === 'progress') {
            setState((prev) => ({
              ...prev,
              progress: {
                current: msg.current,
                total: msg.total,
                percent: msg.percent,
              },
            }));
            // Mirror progress into agentStatusStore for header indicator
            useAgentStatusStore.getState().updateProgress(msg.current, msg.percent);
            return;
          }

          // Ignore new optimizer progress/result types — this hook only handles
          // single-backtest messages. Grid search, walk-forward, and aggregation
          // backtest have their own hooks.
          if (
            msg.type === 'grid_search_progress' ||
            msg.type === 'grid_search_result' ||
            msg.type === 'walk_forward_progress' ||
            msg.type === 'walk_forward_result' ||
            msg.type === 'aggregation_grid_search_progress' ||
            msg.type === 'aggregation_grid_search_result' ||
            msg.type === 'aggregation_walk_forward_progress' ||
            msg.type === 'aggregation_walk_forward_result' ||
            msg.type === 'aggregation_backtest_progress' ||
            msg.type === 'aggregation_backtest_result'
          ) {
            return;
          }

          // type === 'result' — guard against duplicate/late messages
          if (settled) return;
          settled = true;

          clearWorkerTimeout();

          if (msg.success) {
            const backtestResult = buildBacktestResult(msg.result, input, candlesForResult, startedAt);
            setResult(backtestResult);
            setState({ running: false, progress: null, error: null });
            // Persist to database (fire-and-forget — UI already has the result)
            // Pass the idempotency key generated at run start so retries are
            // transparent (the server returns the existing row on replay).
            submitClientBacktest(
              {
                id: backtestResult.id,
                config: {
                  strategyName: input.strategyName,
                  symbol: input.symbol,
                  timeframe: input.timeframe,
                  startDate: new Date(input.startDate).getTime(),
                  endDate: new Date(input.endDate).getTime(),
                  initialCapital: input.initialCapital,
                  exchange: input.exchange ?? 'bybit',
                  params: input.params,
                },
                trades: backtestResult.trades,
                equity: backtestResult.equity,
                metrics: backtestResult.metrics as Record<string, unknown>,
                ...(backtestResult.strategyLogs !== undefined
                  ? { strategyLogs: backtestResult.strategyLogs }
                  : {}),
                // Phase 2: persist perAssetResults and signalHistory for unified shape.
                // For single-asset runs the server synthesizes these, but if the worker
                // already provides them (agg-of-one worker path), send them for completeness.
                ...(backtestResult.perAssetResults != null
                  ? { perAssetResults: backtestResult.perAssetResults as Record<string, unknown> }
                  : {}),
                // strategyConfigId is used by the server to link to agg-of-one automatically.
                // aggregationId is sent if the caller already has one.
                ...(backtestResult.aggregationId != null
                  ? { aggregationId: backtestResult.aggregationId }
                  : {}),
              },
              idempotencyKey,
            ).then(() => {
              // Invalidate history caches so the newly-saved run appears in the list
              // and the selectedBacktestId highlight can match the persisted row.
              queryClient.invalidateQueries({ queryKey: queryKeys.history });
              queryClient.invalidateQueries({ queryKey: ['explorer-history'] });
            }).catch(() => {
              // Silently ignore save failures — result is already displayed.
              // Do NOT invalidate history on failure (the row was not saved).
            });
            worker.terminate();
            workerRef.current = null;
            resolve(backtestResult);
          } else {
            setStoreError(msg.error);
            setState({ running: false, progress: null, error: msg.error });
            worker.terminate();
            workerRef.current = null;
            reject(new Error(msg.error));
          }
        };

        worker.onerror = (e: ErrorEvent) => {
          if (settled) return;
          settled = true;
          clearWorkerTimeout();
          const errorMsg = e.message || 'Worker error';
          setStoreError(errorMsg);
          setState({ running: false, progress: null, error: errorMsg });
          worker.terminate();
          workerRef.current = null;
          reject(new Error(errorMsg));
        };

        // ── 5. Post input to worker ───────────────────────────────────────
        const workerInput: WorkerInput = {
          strategyCode: input.strategyCode,
          candles: fetchedCandles,
          fundingRates,
          openInterest: openInterestRecords,
          longShortRatios: longShortRatioRecords,
          params: input.params,
          config: {
            symbol: input.symbol,
            timeframe: input.timeframe,
            initialCapital: input.initialCapital,
            leverage: input.leverage ?? 1,
            takerFeeRate: input.takerFeeRate ?? 0.001,
            slippagePercent: input.slippagePercent ?? 0.05,
            mode: input.mode ?? 'futures',
            startDate: new Date(input.startDate).getTime(),
            endDate: new Date(input.endDate).getTime(),
          },
          collectDiagnostics: input.collectDiagnostics ?? false,
        };

        worker.postMessage(workerInput);
      });
    },
    [setResult, setRunning, setStoreError, timeoutMs, clearWorkerTimeout, queryClient],
  );

  const cancel = useCallback(() => {
    clearWorkerTimeout();
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
    setStoreError('Cancelled');
    setState({ running: false, progress: null, error: 'Cancelled' });
  }, [setStoreError, clearWorkerTimeout]);

  return { ...state, run, cancel };
}
